import {useDocumentTitle, useToggle} from "usehooks-ts";
import React, {useCallback, useState} from "react";
import useSWR from "swr";
import {getHttpClient, swrFetcher} from "../../utils/api.ts";
import {useAssetSelector} from "./components/useAssetSelector.ts";
import BackButton from "../../components/BackButton.tsx";
import {IoCubeOutline, IoFolderOutline} from "react-icons/io5";
import RenderMediaAsset from "../../components/RenderMediaAsset.tsx";
import {
    FiCheckSquare,
    FiEdit3,
    FiExternalLink,
    FiFilePlus,
    FiFolderPlus,
    FiGrid,
    FiHome,
    FiSquare,
    FiTrash2,
    FiX
} from "react-icons/fi";
import {DeleteModal, DownloadModal, MoveToModal} from "./components/BulkModeTools.tsx";
import Input from "../../components/form/Input.tsx";
import {Link, useLocation, useSearchParams} from "wouter";
import DropzoneUploader from "../../components/uploader/DropzoneUploader.tsx";
import {AssetResponseItem, FolderAsset, MediaAsset} from "../../@types/AssetTypes.ts";
import Drawer from "@/components/Drawer.tsx";
import {closeDrawer, closeModal, openDrawer, openModal} from "@/components/dispatch_functions.ts";
import Modal from "@/components/Modal.tsx";

export default function MediaAssets(){
    useDocumentTitle("Media Assets")
    const [searchParams, setSearchParams] = useSearchParams()

    const folder_id = searchParams.get('folder') || 0
    const {data, isLoading, mutate} = useSWR<{assets: AssetResponseItem[]}>(`/assets?type=image,video,folder,polotno&parent_id=${folder_id}`, swrFetcher)
    const folders = data?.assets?.filter(({type})=>type === "folder")
    const files = data?.assets?.filter(({type})=>type !== "folder");
    const setSelection = useAssetSelector((state)=>state.setItems)
    const selectedItemsCount = useAssetSelector((state)=>state.selectedAssets.length);
    const toggleSelectAll = ()=>{
        setSelection(selectedItemsCount > 0 ? [] : files.map((i)=>i.id));
    }
    const toggleSelectItem = useAssetSelector((state)=>state.toggleSelectItem)
    const selectedItems = useAssetSelector((state)=>state.selectedAssets)
    const [,,getBreadcrumbs] = useFolders()
    // const getBreadcrumbs = ()=>[];
    const [uploadAreaVisible, toggleUploadArea] = useToggle()
    const [multi_select_enabled, toggleMultiSelect] = useState(false)
    const breadCrumbs = getBreadcrumbs(String(folder_id)) as Array<Breadcrumbs>
    const [selectedAsset, setSelectedAsset] = useState<AssetResponseItem>(null)
    return <div>
        <h1 className={"text-4xl mb-3"}>Media Assets</h1>
        {
            folder_id ? <div>
                <BackButton/>
            </div> : null
        }
        {
            breadCrumbs.length > 1 ?
                <div>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb">
                            <li onClick={() => setSearchParams((prev) => {
                                prev.delete('folder');
                                return prev;
                            })} className="breadcrumb-item" aria-current="page">
                                <FiHome/>
                            </li>
                            {
                                breadCrumbs.map((i, index) => {
                                    return <li
                                        onClick={() => setSearchParams((prev) => {
                                            prev.set('folder', i.id);
                                            return prev;
                                        })}
                                        className={`breadcrumb-item ${index + 1 == breadCrumbs.length ? 'active' : ''} `}
                                        aria-current="page">
                                        {i?.name}
                                    </li>
                                })
                            }

                        </ol>
                    </nav>
                </div> : null
        }

        <div className="flex items-center gap-2 border-base-100 border rounded-lg p-2 justify-center">
            <button
                onClick={toggleUploadArea}
                className={'btn btn-link text-decoration-none d-inline-flex align-items-center gap-2'}><FiFilePlus/>
                <span>Upload files</span></button>
            <Link href={"/editor"} className={'btn btn-link text-decoration-none d-inline-flex align-items-center gap-2'}>
                <FiFilePlus /> Create new file
            </Link>
            <CreateNewFolder parent_id={folder_id} mutate={mutate}/>

            {
                folder_id != 0 ?
                <DeleteFolder folderId={String(folder_id)} onDelete={() => {
                    const parent = breadCrumbs[breadCrumbs.length - 2];
                    setSearchParams((prev) => {
                        if (parent) {
                            prev.set('folder', parent.id)
                        } else {
                            prev.delete('folder')
                        }
                        return prev;
                    })
                }}/> : null
            }
        </div>
            {
                uploadAreaVisible ?
                <div className="my-2">
                    <DropzoneUploader onUpload={mutate} parent_id={folder_id}/>
                </div> : null
            }
        {
            !isLoading && files?.length > 1 ?
                <SelectionBar
                    onToggleEnabled={toggleMultiSelect}
                    isEnabled={multi_select_enabled}
                    mutate={mutate} toggleSelectAll={toggleSelectAll}/> : null
        }

        {
            !isLoading && files?.length === 0 ? <p className={"flex flex-col items-center justify-center mt-4"}>
                <IoCubeOutline size={48} className={"text-muted mb-2"} style={{strokeWidth: 1}}/>
                <br/>
                There are no files in this folder
            </p> : null
        }

        <div className="flex gap-2 flex-wrap mb-2">
            {
                folders?.map((i) => {
                    const data= i.data as FolderAsset
                    return <div className={""}>
                        <button
                            className={"btn btn-secondary d-flex align-items-center justify-content-center gap-2 "}
                            onClick={() => setSearchParams((prev) => {
                                prev.set('folder', i.id)
                                return prev;
                            })}><IoFolderOutline style={{marginTop: -1}}/> <span>{data.name}</span></button>
                    </div>
                })
            }
        </div>
        <div className={"grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2"}>
            {
                files?.map((i) => {
                    const isSelected = selectedItems.includes(i.id)
                    return <div className={"col"}
                                key={String(i.id)}
                                onClick={() => {
                                    if(multi_select_enabled) {
                                        toggleSelectItem(i.id)
                                    }else {
                                        setSelectedAsset(i)
                                        openDrawer("media_asset")
                                    }
                                }}>
                        <div className={"border border-base-200 rounded-lg overflow-hidden"} style={{width:'100%'}}>
                            <RenderMediaAsset isSelected={isSelected}
                                              item={i.data as MediaAsset}/>
                        </div>

                    </div>
                })
            }
        </div>
        <ShowAssetInOffCanvas
            onDelete={()=>{
                setSelectedAsset(null)
                mutate()
            }}
            selectedAsset={selectedAsset}
        />
    </div>
}

function ShowAssetInOffCanvas({selectedAsset, onDelete}:{
    selectedAsset: AssetResponseItem,
    onDelete: ()=>void
}){
    const [deleting, setDeleting] = useState(false)
    const mediaAsset = (selectedAsset?.data as MediaAsset)
    const del = ()=>{
        setDeleting(true)
        getHttpClient().delete(`/assets/${selectedAsset.id}`)
            .then(()=>{
                setDeleting(false)
                closeDrawer("media_asset")
                closeModal("delete-asset")
                setTimeout(()=>{
                    onDelete()
                }, 500);
            })
    }
    const [, navigate] = useLocation();

    function createNewStoreAndLoadImage(){
        const data = JSON.stringify({
            width:mediaAsset.width,
            height: mediaAsset.height,
            url: mediaAsset.url,
        })
        console.log(data)
        navigate(`/image_editor/${btoa(data)}`)
    }
    if(!selectedAsset) return null;
    return <React.Fragment>
        <Modal
                submitBtnClass={"btn btn-error"}
                id={"delete-asset"}
                confirmLoading={deleting}
                onSubmit={del}
                okText={"Delete"}
               title={"Delete asset"}>
            <p>Are you sure you want to delete this asset ?</p>
        </Modal>
        <Drawer position={"start"} id={"media_asset"} title={"Media Asset"}>
            {
                mediaAsset?.name ? <h4>{mediaAsset?.name}</h4> : null
            }
            {
                selectedAsset ?
                    <RenderMediaAsset
                        item={mediaAsset}
                        height={400}/> : null
            }
            <ul className="divide-y border border-base-200 mt-3 rounded-lg overflow-hidden divide-base-200">
                {
                    selectedAsset?.type === "polotno" ?
                        <Link
                            className="gap-2 px-3 py-3 hover:text-primary text-base space-between"
                            to={`/editor?id=${selectedAsset.id}`}>
                            Edit Project <FiEdit3/></Link> : null
                }
                {
                    selectedAsset?.type === "image" ?
                        <button
                            className="gap-2 px-3 py-3 hover:text-primary text-base space-between w-full"
                            onClick={createNewStoreAndLoadImage}>
                            Edit Image <FiEdit3/></button> : null
                }

                <a href={mediaAsset?.playURL || mediaAsset?.url}
                   target={"_blank"}
                   className="gap-2 px-3 py-3 hover:text-primary text-base space-between">
                    View file <FiExternalLink/>
                </a>
                <button onClick={() => {
                    openModal('delete-asset')
                }}
                        className="gap-2 px-3 py-3 hover:text-red-700 text-red-500 text-base space-between w-full">
                    Delete
                    asset <FiTrash2/>
                </button>
            </ul>
        </Drawer>
    </React.Fragment>
}


interface Breadcrumbs {
    id: string,
    name: string
}

function useFolders() {
    const {data} = useSWR<{ assets: AssetResponseItem[] }>('/assets?type=folder', swrFetcher);

    const getFolderName = useCallback((folderId: string) => {
        return (data?.assets?.find((i) => i.id == folderId)?.data as FolderAsset)?.name as string;

    }, [data])
    const getParentFolder = useCallback((folderId:  string) => {
        return data?.assets?.find((i) => i.id == folderId)?.parent_id;
    }, [data]);

    const getBreadcrumbs = useCallback((folderId: string) => {
        const breadcrumbs: Array<Breadcrumbs> = []
        let currentFolder = folderId;
        while (currentFolder) {
            const parentFolder = getParentFolder(currentFolder)
            breadcrumbs.push({
                id: currentFolder,
                name: getFolderName(currentFolder)
            })
            if(!parentFolder) break;
            currentFolder = String(parentFolder);
        }
        return breadcrumbs.reverse()
    },[getFolderName, getParentFolder]);


    return [
        getFolderName,
        getParentFolder,
        getBreadcrumbs,
    ]

}

function SelectionBar({mutate, toggleSelectAll, isEnabled, onToggleEnabled}) {
    const selectedItemsCount = useAssetSelector((state)=>state.selectedAssets.length)
    const setItems = useAssetSelector((state)=>state.setItems)
    return <>
        {
            isEnabled ?
                <div className={"rounded border border-base-300 bg-base-200 p-1 mb-2 "}>
                    <div className={"grid grid-cols-1 md:grid-cols-3 gap-2"}>
                            <div className={"inline-flex gap-2 items-center"}>
                                <button className={"btn"} onClick={toggleSelectAll}
                                        title={"Click to select all"}>{
                                    selectedItemsCount === 0 ? <FiSquare/> :
                                        <FiCheckSquare/>
                                }
                                </button>
                                <small style={{width: 90}}>{selectedItemsCount} selected</small>
                            </div>
                        <div className="flex justify-center">
                            <div className="rounded border border-base-200 inline-flex gap-2 align-items-center">
                                <DownloadModal />
                                <MoveToModal mutate={mutate}/>
                                <DeleteModal mutate={mutate}/>
                            </div>
                        </div>
                        <div className={"flex justify-end"}>
                            {/*<button className={"btn"}>*/}
                            {/*    <FiGrid/>*/}
                            {/*</button>*/}
                            <button className={"btn"} onClick={() => {
                                onToggleEnabled()
                                setItems([])
                            }}>
                                <FiX/>
                            </button>
                        </div>

                    </div></div> : <div className={"text-end"}>
                    <button onClick={onToggleEnabled}
                            className={"btn text-decoration-none d-inline-flex align-items-center gap-2 btn-link"}><FiGrid/> Bulk Actions
                    </button>
                </div>
        }
    </>
}

function DeleteFolder({folderId,
                      onDelete}:{
    folderId: string,
    onDelete: ()=>void
}){
    const [deleting, setDeleting] = useState(false)
    const delFolder = ()=>{
        setDeleting(true)
        getHttpClient().delete(`/assets/${folderId}`).then(()=>{
            closeModal('delete-folder')
            onDelete()
            setDeleting(false)
        })
    }
    return <>
        <Modal
            id={"delete-folder"}
            confirmLoading={deleting}
            onSubmit={delFolder}
            submitBtnClass={"btn btn-error"}
            okText={"Delete"}
            title={"Delete folder"}>
            <p>Are you sure you want to delete this folder? This will also delete the content of this folder</p>
        </Modal>
        <button onClick={()=>{
            openModal('delete-folder')
        }} className={"btn btn-link d-inline-flex align-items-center gap-2 text-decoration-none text-danger"}>
            <FiTrash2/>Delete folder
        </button>
    </>
}



function CreateNewFolder({mutate, parent_id}) {
    const [name, setName] = useState('')
    const [creating, setCreating] = useState(false)
    const createFolder = ()=>{
        setCreating(true)
        getHttpClient().post('/assets',{
            data:{
                name,
            },
            parent_id,
            type:'folder',
        }).then(()=>{
            setCreating(false)
            mutate()
            closeModal('new-folder')
        })
    }
    return <>
        <Modal title={"New folder"}
               disabled={!name}
               id={"new-folder"}
               confirmLoading={creating}
               onSubmit={createFolder}
               okText={"Submit"}>
            <Input value={name} label={"Folder name"} placeholder={"Folder name"} onChange={setName} />
        </Modal>
        <button onClick={()=>openModal('new-folder')}
                className={"btn btn-link text-decoration-none d-inline-flex gap-2 align-items-center"}>
            <FiFolderPlus/>
            Create new folder
        </button>

    </>
}
